/**
 * S11 · Timeline — real data edition. Renders a session header + a list
 * of its captures, grouped by capture-kind icon. Keeps the inline-style
 * fidelity from the showcase; the radial FAB opens the mode picker at
 * `/sessions/:id/modes`.
 */
import { Fragment, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { Capture } from '@kapture/types';
import { sourceMarker } from '@kapture/ui/tokens';
import {
  Chip,
  GestureBar,
  I,
  Ico,
  PhotoPlaceholder,
  ScreenFrame,
  StatusBar,
  T,
  Waveform,
} from '@/components/primitives';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';
import { useCapturesBySession, useFieldValues, useSession } from '@kapture/client-core';
import { useBlobUrl } from '@/lib/device/useBlobUrl';
import { getTemplate } from '@kapture/domain';

export const Route = createFileRoute('/sessions/$sessionId')({
  component: TimelinePage,
  staticData: { mobile: true },
});

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function TimelinePage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId">
      <TimelineContent />
    </LayoutOrLeaf>
  );
}

function TimelineContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session, isLoading: sessionLoading } = useSession(sessionId);
  const { data: captures, isLoading: capturesLoading } = useCapturesBySession(sessionId);
  const { data: fieldValues } = useFieldValues(sessionId);
  const template = getTemplate(session?.templateId);
  const formProgress = template
    ? {
        total: template.fields.length,
        done: (fieldValues ?? []).filter((v) => template.fields.some((f) => f.id === v.fieldId))
          .length,
      }
    : null;

  const counts = useMemo(() => {
    const list = captures ?? [];
    return {
      total: list.length,
      photos: list.filter((c) => c.kind === 'photo').length,
      voice: list.filter((c) => c.kind === 'voice').length,
      notes: list.filter((c) => c.kind === 'note').length,
    };
  }, [captures]);

  if (sessionLoading) {
    return (
      <ScreenFrame tone="light">
        <StatusBar />
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            fontFamily: T.font,
            color: T.textMute,
            fontSize: 13,
          }}
        >
          Loading session…
        </div>
      </ScreenFrame>
    );
  }
  if (!session) {
    return (
      <ScreenFrame>
        <div
          style={{
            padding: 24,
            fontFamily: T.font,
            color: T.textDim,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Session not found</div>
          <Link
            to="/"
            style={{
              color: T.brandText,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              fontFamily: T.font,
            }}
          >
            ← Back to sessions
          </Link>
        </div>
      </ScreenFrame>
    );
  }

  const isActive = session.status === 'active';

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <Link
          to="/"
          aria-label="Back to sessions"
          style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
        >
          <Ico d={I.back} size={20} />
        </Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              fontFamily: T.font,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {session.title}
          </div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {counts.total} capture{counts.total === 1 ? '' : 's'} · {session.status}
          </div>
        </div>
        {isActive && (
          <Chip
            tone="brandSoft"
            size="sm"
            icon={<span style={{ width: 6, height: 6, borderRadius: 3, background: T.brand }} />}
          >
            Live
          </Chip>
        )}
        <Ico d={I.moreV} size={18} />
      </div>
      {formProgress && (
        <Link
          to="/sessions/$sessionId/form"
          params={{ sessionId }}
          preload="intent"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            borderBottom: `1px solid ${T.stroke}`,
            background: T.surface2,
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background:
                formProgress.done === formProgress.total ? T.goodSoft : sourceMarker.form.soft,
              color: formProgress.done === formProgress.total ? T.good : sourceMarker.form.base,
              border: `1px solid ${
                formProgress.done === formProgress.total ? T.good : sourceMarker.form.base
              }40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ico d={I.file} size={14} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                fontFamily: T.font,
                letterSpacing: '-0.01em',
              }}
            >
              {template?.name} form
            </div>
            <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
              {formProgress.done} of {formProgress.total} fields filled
            </div>
          </div>
          <Ico d={I.chev} size={16} stroke={T.textMute} />
        </Link>
      )}
      <div
        style={{ padding: '10px 16px', display: 'flex', gap: 6, overflowX: 'auto', flexShrink: 0 }}
      >
        <Chip tone="ink" size="sm">
          All {counts.total}
        </Chip>
        <Chip tone="neutral" size="sm" icon={<Ico d={I.camera} size={10} />}>
          Photos {counts.photos}
        </Chip>
        <Chip tone="neutral" size="sm" icon={<Ico d={I.mic} size={10} />}>
          Voice {counts.voice}
        </Chip>
        <Chip tone="neutral" size="sm" icon={<Ico d={I.note} size={10} />}>
          Notes {counts.notes}
        </Chip>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        {capturesLoading && (
          <div
            style={{
              padding: '40px 0',
              color: T.textMute,
              fontSize: 13,
              textAlign: 'center',
              fontFamily: T.font,
            }}
          >
            Loading captures…
          </div>
        )}
        {!capturesLoading && counts.total === 0 && (
          <div
            style={{
              padding: '48px 12px',
              textAlign: 'center',
              color: T.textDim,
              fontFamily: T.font,
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
              No captures yet
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              Tap the orange + to add a note, photo, voice memo, or pin.
            </div>
          </div>
        )}
        {(captures ?? []).map((c, i) => (
          <Fragment key={c.id}>
            {c.subjectRef !== (captures ?? [])[i - 1]?.subjectRef && (
              <SubjectDivider subject={c.subjectRef} first={i === 0} />
            )}
            <TimelineEvent capture={c} sessionId={sessionId} />
          </Fragment>
        ))}
      </div>

      <div
        style={{
          position: 'relative',
          padding: '8px 16px 14px',
          borderTop: `1px solid ${T.stroke}`,
          background: T.surface,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Link
          to="/sessions/$sessionId/review"
          params={{ sessionId }}
          preload="intent"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            borderRadius: 10,
            border: `1px solid ${T.stroke}`,
            background: T.surface,
            color: T.text,
            fontFamily: T.font,
            fontWeight: 600,
            fontSize: 12,
            textDecoration: 'none',
          }}
        >
          <Ico d={I.sparkle} size={12} fill={T.brand} stroke={T.brand} />
          Review
        </Link>
        <button
          type="button"
          aria-label="Add capture"
          onClick={() => navigate({ to: '/sessions/$sessionId/modes', params: { sessionId } })}
          style={{
            width: 58,
            height: 58,
            borderRadius: 29,
            background: T.brand,
            color: T.brandInk,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            boxShadow: '0 8px 20px rgba(255,90,31,0.4), 0 2px 6px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            position: 'relative',
            // Sits above any future floating UI (devtools badges, toast
            // stacks) that might drift into the bottom-centre of the
            // mobile chrome.
            zIndex: 40,
          }}
        >
          <Ico d={I.plus} size={28} sw={2.4} />
        </button>
        <div style={{ width: 92 }} aria-hidden />
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

/**
 * Where the walk moved from one thing to the next.
 *
 * The timeline stays in capture order rather than regrouping into buckets,
 * because the order *is* the record — it is what the inspector did, and a
 * reviewer reading a fire-door round wants to see the round. A rule when the
 * subject changes gives them the forty bundles without rearranging anything,
 * and site-level evidence between subjects stays where it happened instead of
 * being swept into a bin at the top.
 *
 * The site-level rule is drawn only where it separates something: a session
 * that never sets a subject is every session today, and a heading over every
 * one of them would be noise on the common case.
 */
function SubjectDivider({ subject, first }: { subject: string | null; first: boolean }) {
  if (!subject && first) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: '14px 0 2px',
      }}
    >
      {subject ? (
        <>
          <Ico d={I.tag} size={13} stroke={T.brandText} />
          <span
            style={{
              fontFamily: T.mono,
              fontSize: 12,
              fontWeight: 700,
              color: T.text,
              wordBreak: 'break-all',
            }}
          >
            {subject}
          </span>
        </>
      ) : (
        <span
          style={{
            fontFamily: T.mono,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: T.textMute,
          }}
        >
          Site level
        </span>
      )}
      <div style={{ flex: 1, height: 1, background: T.stroke }} />
    </div>
  );
}

function TimelineEvent({ capture, sessionId }: { capture: Capture; sessionId: string }) {
  return (
    <Link
      to="/sessions/$sessionId/captures/$captureId"
      params={{ sessionId, captureId: capture.id }}
      preload="intent"
      style={{
        display: 'block',
        padding: '10px 0',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: T.textMute,
          fontFamily: T.mono,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          padding: '0 0 6px',
        }}
      >
        {timeLabel(capture.createdAt)}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div
          style={{
            width: 2,
            background: T.stroke,
            marginLeft: 6,
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            {/* Evidence DNA source marker — colour + icon per source family,
                shared with the web ledger via @kapture/ui tokens. */}
            <div
              role="img"
              aria-label={markerFor(capture.kind).label}
              style={{
                width: 20,
                height: 20,
                borderRadius: 7,
                background: markerFor(capture.kind).soft,
                color: markerFor(capture.kind).base,
                border: `1px solid ${markerFor(capture.kind).base}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: -15,
              }}
            >
              <Ico d={iconFor(capture.kind)} size={11} />
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                fontFamily: T.font,
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {titleFor(capture)}
            </div>
            {Object.keys(capture.aiFields).length > 0 && (
              <span
                style={{
                  fontSize: 9,
                  fontFamily: T.mono,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: '#5B4CC4',
                  border: '1px solid #7B6BE055',
                  background: '#7B6BE014',
                  borderRadius: 6,
                  padding: '1px 6px',
                  flexShrink: 0,
                }}
              >
                AI · {Object.keys(capture.aiFields).length} field
                {Object.keys(capture.aiFields).length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <EventBody capture={capture} />
        </div>
      </div>
    </Link>
  );
}

const KIND_TO_SOURCE: Record<Capture['kind'], keyof typeof sourceMarker> = {
  photo: 'photo',
  video: 'video',
  voice: 'voice',
  note: 'note',
  gps: 'location',
  barcode: 'form',
  // `form`, because that is where a signature field lives and there is no
  // signature marker in the ledger. Adding one would be a design-system
  // change for a single evidence type.
  signature: 'form',
};

function markerFor(kind: Capture['kind']) {
  return sourceMarker[KIND_TO_SOURCE[kind]];
}

function iconFor(kind: Capture['kind']): string | React.ReactNode {
  switch (kind) {
    case 'photo':
      return I.camera;
    case 'video':
      return I.video;
    case 'voice':
      return I.mic;
    case 'note':
      return I.note;
    case 'gps':
      return I.pin;
    case 'barcode':
      return I.tag;
    default:
      return I.note;
  }
}

function titleFor(c: Capture): string {
  switch (c.kind) {
    case 'note':
      return c.title || 'Untitled note';
    case 'voice':
      return 'Voice note';
    case 'photo':
      return 'Photo';
    case 'gps':
      return 'Location pin';
    case 'barcode':
      return `Asset · ${c.value ?? 'scan'}`;
    default:
      return 'Capture';
  }
}

function PhotoThumb({ blobPath }: { blobPath: string | null }) {
  const url = useBlobUrl(blobPath);
  if (url) {
    return (
      <img
        src={url}
        alt=""
        style={{ width: 100, height: 72, objectFit: 'cover', flexShrink: 0, display: 'block' }}
      />
    );
  }
  return (
    <div style={{ width: 100, height: 72, flexShrink: 0 }}>
      <PhotoPlaceholder h={72} w={100} tone="cloudy" />
    </div>
  );
}

function EventBody({ capture }: { capture: Capture }) {
  if (capture.kind === 'note') {
    return (
      <div
        style={{
          padding: 10,
          borderRadius: 10,
          border: `1px solid ${T.stroke}`,
          background: T.surface,
          fontSize: 12,
          color: T.textDim,
          fontFamily: T.font,
          lineHeight: 1.5,
        }}
      >
        {capture.body || <span style={{ color: T.textMute }}>(empty)</span>}
      </div>
    );
  }
  if (capture.kind === 'voice') {
    return (
      <div
        style={{
          padding: 10,
          borderRadius: 10,
          border: `1px solid ${T.stroke}`,
          background: T.surface,
        }}
      >
        <Waveform bars={20} active={0} color={T.textDim} bg={T.stroke2} h={20} />
        {capture.transcript && (
          <div
            style={{
              fontSize: 11,
              color: T.textDim,
              fontFamily: T.font,
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            “{capture.transcript}”
          </div>
        )}
        <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.mono, marginTop: 4 }}>
          {Math.round((capture.durationMs ?? 0) / 1000)}s
        </div>
      </div>
    );
  }
  if (capture.kind === 'photo') {
    return (
      <div
        style={{
          display: 'flex',
          gap: 6,
          borderRadius: 10,
          overflow: 'hidden',
          border: `1px solid ${T.stroke}`,
        }}
      >
        <PhotoThumb blobPath={capture.blobPath} />
        <div style={{ flex: 1, padding: '6px 8px' }}>
          <div style={{ fontSize: 11, color: T.text, fontFamily: T.font, lineHeight: 1.3 }}>
            {capture.tags.length
              ? `${capture.tags.length} tag${capture.tags.length === 1 ? '' : 's'}`
              : capture.width && capture.height
                ? `${capture.width}×${capture.height}`
                : 'Untagged'}
          </div>
          {capture.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              {capture.tags.slice(0, 2).map((tag) => (
                <Chip key={tag} tone="brandSoft" size="sm">
                  {tag}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (capture.kind === 'barcode') {
    return (
      <div
        style={{
          padding: 10,
          borderRadius: 10,
          border: `1px solid ${T.stroke}`,
          background: T.surface,
          fontFamily: T.mono,
          fontSize: 12,
          color: T.text,
          wordBreak: 'break-all',
        }}
      >
        <span
          style={{
            color: T.textMute,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {capture.format}
        </span>{' '}
        · {capture.value}
      </div>
    );
  }
  if (capture.kind === 'gps') {
    const g = capture.gps;
    return (
      <div
        style={{
          padding: 10,
          borderRadius: 10,
          border: `1px solid ${T.stroke}`,
          background: T.surface,
          fontFamily: T.mono,
          fontSize: 11,
          color: T.textDim,
        }}
      >
        {g ? `${g.lat.toFixed(5)}, ${g.lng.toFixed(5)}  · ±${g.accuracyMeters ?? '?'}m` : 'No fix'}
      </div>
    );
  }
  return null;
}
